import { useEffect, useMemo, useState } from 'react';
import { loadYandexMetrika } from '../utils/yandexMetrika';
import './CookieConsentBanner.css';

type ConsentState = {
  necessary: true;
  analytics: boolean;
  functional: boolean;
};

const STORAGE_KEY = 'recvi_cookie_consent_v1';

function readConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      functional: Boolean(parsed.functional),
    };
  } catch {
    return null;
  }
}

function writeConsent(consent: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
}

export default function CookieConsentBanner() {
  const [consent, setConsent] = useState<ConsentState | null>(() => readConsent());
  const [open, setOpen] = useState(false);

  const hasDecision = useMemo(() => consent !== null, [consent]);

  useEffect(() => {
    if (consent?.analytics) {
      loadYandexMetrika();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptAll = () => {
    const next: ConsentState = { necessary: true, analytics: true, functional: true };
    writeConsent(next);
    setConsent(next);
    loadYandexMetrika();
  };

  const acceptNecessaryOnly = () => {
    const next: ConsentState = { necessary: true, analytics: false, functional: false };
    writeConsent(next);
    setConsent(next);
  };

  const saveSettings = (next: ConsentState) => {
    writeConsent(next);
    setConsent(next);
    setOpen(false);
    if (next.analytics) {
      loadYandexMetrika();
    }
  };

  if (hasDecision) return null;

  return (
    <>
      <div className="cc-banner" role="dialog" aria-label="Cookies" aria-live="polite">
        <div className="cc-card">
          <div className="cc-text">
            <div className="cc-title">Cookies</div>
            <div className="cc-desc">
              Мы используем cookies для корректной работы сайта и (с вашего согласия) — для аналитики и улучшения
              сервиса. Подробнее — в <a className="cc-link" href="/cookies">Политике использования Cookies</a>.
            </div>
          </div>
          <div className="cc-actions">
            <button className="cc-btn cc-btn-secondary" onClick={() => setOpen(true)} type="button">
              Настроить
            </button>
            <button className="cc-btn cc-btn-ghost" onClick={acceptNecessaryOnly} type="button">
              Только необходимые
            </button>
            <button className="cc-btn cc-btn-primary" onClick={acceptAll} type="button">
              Принять все
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="cc-modal-backdrop" role="dialog" aria-label="Настройка cookies">
          <div className="cc-modal">
            <div className="cc-modal-head">
              <div className="cc-modal-title">Настройка cookies</div>
              <button className="cc-icon-btn" onClick={() => setOpen(false)} type="button" aria-label="Закрыть">
                ×
              </button>
            </div>

            <div className="cc-options">
              <label className="cc-option">
                <input type="checkbox" checked disabled />
                <div>
                  <div className="cc-option-title">Технические (обязательные)</div>
                  <div className="cc-option-desc">Нужны для работы сайта и авторизации. Отключить нельзя.</div>
                </div>
              </label>

              <label className="cc-option">
                <input
                  type="checkbox"
                  checked={Boolean(consent?.analytics)}
                  onChange={(e) => setConsent({ necessary: true, analytics: e.target.checked, functional: Boolean(consent?.functional) })}
                />
                <div>
                  <div className="cc-option-title">Аналитические</div>
                  <div className="cc-option-desc">Помогают улучшать сайт (например, Яндекс.Метрика).</div>
                </div>
              </label>

              <label className="cc-option">
                <input
                  type="checkbox"
                  checked={Boolean(consent?.functional)}
                  onChange={(e) => setConsent({ necessary: true, analytics: Boolean(consent?.analytics), functional: e.target.checked })}
                />
                <div>
                  <div className="cc-option-title">Функциональные</div>
                  <div className="cc-option-desc">Запоминают настройки интерфейса и предпочтения.</div>
                </div>
              </label>
            </div>

            <div className="cc-modal-actions">
              <button className="cc-btn cc-btn-ghost" onClick={acceptNecessaryOnly} type="button">
                Отклонить необязательные
              </button>
              <button
                className="cc-btn cc-btn-primary"
                onClick={() =>
                  saveSettings(
                    consent ?? { necessary: true, analytics: false, functional: false },
                  )
                }
                type="button"
              >
                Сохранить выбор
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

